import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatDialog } from '@angular/material/dialog';
import { Subject, Subscription } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { AdvanceSearchService } from '../../../services/Advance-search/advance-search.service';
import { ToastService } from '../../../services/toast/toast.service';
import { ChangePrimaryVerifyOtpPopupComponent } from '../change-primary-verify-otp-popup/change-primary-verify-otp-popup.component';
import { PincodeLookupController } from '../../../common-files/pincode-lookup';
import { buildAccommodationAgreement } from '../../../common-files/accommodation-agreement';

@Component({
  selector: 'app-edit-primary-devotee',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, MatSelectModule],
  templateUrl: './edit-primary-devotee.component.html',
  styleUrl: './edit-primary-devotee.component.scss',
})
export class EditPrimaryDevoteeComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isOpen: boolean = false;
  @Input() devoteeDetails: any[] = [];
  @Input() oldDevoteeDetails: any[] = [];
  @Output() close = new EventEmitter<boolean>();

  readonly familyStage = 'Choose Family Relations';
  readonly visitorStage = 'Choose Visitor Relations';
  readonly contactStage = 'Change Contact No.';
  readonly addressStage = 'Address details';
  readonly agreementStage = 'Agreement to guidelines(on behalf of the requestor)';

  maleRelations: string[] = [
    'Father', 'Son', 'Brother', 'Husband', 'Son-In-Law', 'Brother-In-Law',
    'Grand-Father', 'Grand-Son', 'Uncle', 'Cousin', 'Relative Others',
    'GuruBhai Attached with Mandir', 'GuruBhai Others', 'Non-Initiated Male',
  ];
  femaleRelations: string[] = [
    'Mother', 'Daughter', 'Sister', 'Wife', 'Daughter-In-Law', 'Sister-In-Law',
    'Grand-Mother', 'Grand-Daughter', 'Aunt', 'Cousin', 'Relative Others',
    'GuruBhai Attached with Mandir', 'GuruBhai Others', 'Non-Initiated Female',
  ];

  steppers: string[] = [];
  step: number = 0;
  primaryDevotee: any;
  visitor: any[] = [];
  contactNumbers: any[] = [];
  addressDetails: any[] = [];
  selectedNumber: string = '';
  isValidNumber = false;
  errorMessage = '';
  isCheckingNumber = false;
  isLoadingVisitors = false;
  isLoadingContacts = false;
  isSubmitting = false;
  addressfields!: FormGroup;
  agreementDetails!: FormGroup;
  pincodeLookup!: PincodeLookupController;
  relationMapperList: any[] = [];
  visitorMapperList: any[] = [];
  oldPrimaryDevotee: any[] = [];
  editMembers: any[] = [];
  editVisitor: any[] = [];
  primaryRelation: string = 'SELF';
  visitorRelation: string = '';

  private initialized = false;
  private contactNumbersLoadedFor: string | null = null;
  private addressLoadedFor: string | number | null = null;
  private visitorRequest?: Subscription;
  private contactNumbersRequest?: Subscription;
  private addressRequest?: Subscription;
  private availabilityRequest?: Subscription;
  private submitRequest?: Subscription;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private advanceService: AdvanceSearchService,
    private fb: FormBuilder,
    private toaster: ToastService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.addressfields = this.fb.group({
      houseNo: ['', Validators.required],
      locality: ['', Validators.required],
      landmark: [''],
      city: ['', Validators.required],
      pincode: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
      district: [{ value: '', disabled: true }, Validators.required],
      state: [{ value: '', disabled: true }, Validators.required],
      country: [{ value: '', disabled: true }, Validators.required],
    });
    this.pincodeLookup = new PincodeLookupController(this.addressfields, this.advanceService);
    this.pincodeLookup.start();

    this.agreementDetails = this.fb.group({
      fullName: ['', Validators.required],
      guardianName: ['', Validators.required],
      place: ['', Validators.required],
      relation: [false, Validators.required],
    });
    this.initialized = true;
    if (this.isOpen) this.initializeFlow();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.isOpen || !this.initialized) return;
    if (changes['isOpen'] || changes['devoteeDetails'] || changes['oldDevoteeDetails']) {
      this.initializeFlow();
    }
  }

  ngOnDestroy(): void {
    this.cancelFlowRequests();
    this.pincodeLookup?.destroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get currentStage(): string {
    return this.steppers[this.step] || '';
  }

  get districtList(): string[] {
    return this.pincodeLookup?.districtList ?? [];
  }

  get isTransitionDisabled(): boolean {
    return this.isLoadingVisitors || this.isLoadingContacts || this.isCheckingNumber;
  }

  get isSubmitDisabled(): boolean {
    if (this.isSubmitting || this.isCheckingNumber || !this.selectedNumber || !this.isValidNumber) {
      return true;
    }
    if (this.currentStage !== this.agreementStage) return false;
    const address = this.addressfields?.getRawValue();
    return !address
      || this.addressfields.invalid
      || !this.pincodeLookup.isVerified(address.pincode)
      || this.agreementDetails.invalid;
  }

  onDistrictChange(): void {
    this.pincodeLookup.resolveSelectedDistrict();
  }

  onClose(): void {
    this.close.emit(false);
  }

  getRelations(member: any): string[] {
    if (!member?.gender) return [];
    return member.gender.toLowerCase() === 'male' ? this.maleRelations : this.femaleRelations;
  }

  getMemberRelation(item: any, currValue: string): void {
    this.upsertMapping(this.relationMapperList, item.devoteeId, item.gender, currValue);
  }

  getVisitorRelation(item: any, currValue: string): void {
    this.upsertMapping(this.visitorMapperList, item.visitorId, item.gender, currValue);
  }

  incrementStep(): void {
    if (this.isTransitionDisabled) return;

    switch (this.currentStage) {
      case this.familyStage:
        if (!this.hasAllFamilyRelations()) {
          this.toaster.error(
            'Kindly specify the relationship of each family member with the requestor to proceed further.',
          );
          return;
        }
        this.relationMapperList = this.buildFamilyMappings();
        this.step += 1;
        if (this.steppers[this.step] === this.contactStage) this.getContactNumbers();
        return;

      case this.visitorStage:
        if (!this.hasAllVisitorRelations()) {
          this.toaster.error(
            'Kindly specify the relationship of each visitor member with the requestor to proceed further.',
          );
          return;
        }
        this.visitorMapperList = this.buildVisitorMappings();
        this.step += 1;
        this.getContactNumbers();
        return;

      case this.contactStage:
        if (!this.selectedNumber || !this.isValidNumber) return;
        this.step += 1;
        this.getDevoteeAddress();
        return;

      case this.addressStage:
        if (!this.hasValidAddressLocation()) return;
        this.prepareAgreement();
        this.step += 1;
        return;
    }
  }

  decrementStep(): void {
    this.step = Math.max(0, this.step - 1);
  }

  onNumberChange(): void {
    this.availabilityRequest?.unsubscribe();
    if (!this.selectedNumber) {
      this.isValidNumber = false;
      this.isCheckingNumber = false;
      this.errorMessage = '';
      return;
    }

    const requestedNumber = this.selectedNumber;
    this.isCheckingNumber = true;
    this.isValidNumber = false;
    this.errorMessage = '';
    this.availabilityRequest = this.advanceService.fetchAvailableContactNumber({
      contactNumber: requestedNumber,
      familyCode: this.primaryDevotee.devoteeFamilyCode,
    }).pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        if (this.selectedNumber === requestedNumber) this.isCheckingNumber = false;
      }),
    ).subscribe({
      next: (res: any) => {
        if (this.selectedNumber !== requestedNumber) return;
        this.isValidNumber = res?.success === true && res?.data?.available === true;
        this.errorMessage = this.isValidNumber
          ? ''
          : res?.message || 'Contact number already exists';
      },
      error: (err: any) => {
        if (this.selectedNumber !== requestedNumber) return;
        this.isValidNumber = false;
        this.errorMessage = err?.error?.message || err?.message || 'Could not verify contact number.';
        this.toaster.error(this.errorMessage);
      },
    });
  }

  finalSubmit(): void {
    if (this.isSubmitting) return;
    if (!this.hasAllFamilyRelations() || !this.hasAllVisitorRelations()) {
      this.toaster.error('Complete all relationship selections before submitting.');
      return;
    }
    if (!this.selectedNumber || !this.isValidNumber) {
      this.toaster.error('Select a valid contact number before submitting.');
      return;
    }
    if (!this.hasValidAddressLocation()) return;
    if (this.agreementDetails.invalid) {
      this.agreementDetails.markAllAsTouched();
      return;
    }

    this.relationMapperList = this.buildFamilyMappings();
    this.visitorMapperList = this.buildVisitorMappings();
    const addressData = this.addressfields.getRawValue();
    const payload = {
      devoteeId: this.primaryDevotee.devoteeId,
      familyCode: this.primaryDevotee.devoteeFamilyCode,
      contactNumber: this.selectedNumber,
      memberRelationship: [...this.relationMapperList],
      visitorRelationship: [...this.visitorMapperList],
      addressLine1: addressData.houseNo,
      addressLine2: addressData.locality,
      addressLine3: addressData.landmark,
      city: addressData.city,
      pin: addressData.pincode,
      state: addressData.state,
      district: addressData.district,
      agreementContent: buildAccommodationAgreement({
        fullName: this.agreementDetails.get('fullName')?.value,
        relationship: this.agreementDetails.get('relation')?.value,
        guardianName: this.agreementDetails.get('guardianName')?.value,
        place: this.agreementDetails.get('place')?.value,
      }),
      place: this.agreementDetails.get('place')?.value,
    };

    this.isSubmitting = true;
    this.submitRequest = this.advanceService.fetchPrimaryRequest(payload).pipe(
      takeUntil(this.destroy$),
      finalize(() => { this.isSubmitting = false; }),
    ).subscribe({
      next: (res: any) => {
        if (!res?.success) {
          this.errorMessage = res?.message || 'Failed to initiate OTP verification';
          return;
        }
        const dialogRef = this.dialog.open(ChangePrimaryVerifyOtpPopupComponent, {
          width: '400px',
          disableClose: true,
          data: {
            sessionId: res.data.sessionId,
            familyCode: this.primaryDevotee.devoteeFamilyCode,
            contactNumber: this.selectedNumber,
          },
        });
        dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((saved) => {
          if (saved) this.close.emit(true);
        });
      },
      error: (err: any) => {
        this.errorMessage = err?.error?.message || 'Failed to change primary devotee';
        this.toaster.error(this.errorMessage);
      },
    });
  }

  getDevoteeAddress(): void {
    const devoteeId = this.primaryDevotee?.devoteeId;
    if (
      !devoteeId
      || this.addressLoadedFor === devoteeId
      || (this.addressRequest && !this.addressRequest.closed)
    ) {
      return;
    }

    this.addressRequest = this.advanceService.fetchDevoteeAddress(devoteeId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.addressDetails = res?.data || [];
          const address = this.addressDetails[0];
          if (!address) {
            this.toaster.error('No address found');
            return;
          }
          this.addressLoadedFor = devoteeId;
          this.patchSavedAddress(address);
        },
        error: (err) => this.toaster.error(err?.error?.message || err?.message),
      });
  }

  importAddress(): void {
    const currentPrimary = this.oldPrimaryDevotee[0];
    if (!currentPrimary) {
      this.toaster.error('No primary devotee found');
      return;
    }

    this.advanceService.fetchDevoteeAddress(currentPrimary.devoteeId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const address = res?.data?.[0];
          if (!address) {
            this.toaster.error('No address found for the primary devotee');
            return;
          }
          this.patchSavedAddress(address);
          this.toaster.success('Address imported successfully');
        },
        error: (err) => this.toaster.error(
          err?.error?.message || err?.message || 'Failed to import address',
        ),
      });
  }

  private initializeFlow(): void {
    this.resetFlowState();
    this.primaryDevotee = this.devoteeDetails[0];
    if (!this.primaryDevotee) return;

    const seen = new Set<unknown>();
    this.editMembers = this.devoteeDetails
      .filter((member) => {
        if (seen.has(member?.devoteeId)) return false;
        seen.add(member?.devoteeId);
        return true;
      })
      .map((member) => ({
        ...member,
        relationshipWithPrimaryDevotee:
          member.devoteeId === this.primaryDevotee.devoteeId ? 'SELF' : '',
      }));
    this.relationMapperList = this.buildFamilyMappings();
    const originalFamily = this.oldDevoteeDetails.length
      ? this.oldDevoteeDetails
      : this.devoteeDetails;
    this.oldPrimaryDevotee = originalFamily.filter((member) => member.isPrimaryDevotee);
    this.getVisitor(this.primaryDevotee.devoteeFamilyCode);
  }

  private resetFlowState(): void {
    this.cancelFlowRequests();
    this.step = 0;
    this.steppers = this.stepsForVisitors(true);
    this.primaryDevotee = undefined;
    this.oldPrimaryDevotee = [];
    this.visitor = [];
    this.editVisitor = [];
    this.editMembers = [];
    this.relationMapperList = [];
    this.visitorMapperList = [];
    this.contactNumbers = [];
    this.addressDetails = [];
    this.selectedNumber = '';
    this.isValidNumber = false;
    this.errorMessage = '';
    this.isCheckingNumber = false;
    this.isLoadingVisitors = false;
    this.isLoadingContacts = false;
    this.isSubmitting = false;
    this.contactNumbersLoadedFor = null;
    this.addressLoadedFor = null;
    this.addressfields?.reset({
      houseNo: '', locality: '', landmark: '', city: '', pincode: '',
      district: { value: '', disabled: true },
      state: { value: '', disabled: true },
      country: { value: '', disabled: true },
    });
    this.agreementDetails?.reset({
      fullName: '', guardianName: '', place: '', relation: false,
    });
  }

  private getVisitor(familyCode: string): void {
    if (!familyCode) {
      this.steppers = this.stepsForVisitors(false);
      return;
    }
    this.isLoadingVisitors = true;
    this.visitorRequest = this.advanceService.fetchVisitorsDetails(familyCode).pipe(
      takeUntil(this.destroy$),
      finalize(() => { this.isLoadingVisitors = false; }),
    ).subscribe({
      next: (res) => {
        this.visitor = Array.isArray(res?.data) ? res.data : [];
        this.editVisitor = this.visitor.map((visitor) => ({ ...visitor, relationship: '' }));
        this.steppers = this.stepsForVisitors(this.editVisitor.length > 0);
      },
      error: (err) => {
        this.steppers = this.stepsForVisitors(false);
        this.toaster.error(err?.error?.message || err?.message);
      },
    });
  }

  private getContactNumbers(): void {
    const familyCode = String(this.primaryDevotee?.devoteeFamilyCode ?? '');
    if (
      !familyCode
      || this.contactNumbersLoadedFor === familyCode
      || (this.contactNumbersRequest && !this.contactNumbersRequest.closed)
    ) {
      return;
    }
    this.isLoadingContacts = true;
    this.contactNumbersRequest = this.advanceService.fetchContactNumbers(familyCode).pipe(
      takeUntil(this.destroy$),
      finalize(() => { this.isLoadingContacts = false; }),
    ).subscribe({
      next: (res: any) => {
        this.contactNumbers = res?.data?.contactNumbers || [];
        this.contactNumbersLoadedFor = familyCode;
      },
      error: (err) => this.toaster.error(err?.error?.message || err?.message),
    });
  }

  private prepareAgreement(): void {
    const fullName = [
      this.primaryDevotee?.devoteeFirstName,
      this.primaryDevotee?.devoteeMiddleName,
      this.primaryDevotee?.devoteeLastName,
    ].filter(Boolean).join(' ');
    this.agreementDetails.patchValue({
      fullName,
      guardianName: this.primaryDevotee?.guardianName || '',
      place: this.addressfields.get('city')?.value,
    });
  }

  private patchSavedAddress(address: any): void {
    this.addressfields.patchValue({
      houseNo: address?.addressLine1 || '',
      locality: address?.addressLine2 || '',
      landmark: address?.addressLine3 || '',
      city: address?.city || '',
      pincode: address?.pin || '',
      district: address?.district || '',
      state: address?.state || '',
      country: address?.country || '',
    }, { emitEvent: false });
    this.pincodeLookup.syncFromForm();
  }

  private hasAllFamilyRelations(): boolean {
    return this.editMembers
      .filter((member) => member.devoteeId !== this.primaryDevotee?.devoteeId)
      .every((member) => Boolean(String(member.relationshipWithPrimaryDevotee || '').trim()));
  }

  private hasAllVisitorRelations(): boolean {
    return this.editVisitor.every(
      (visitor) => Boolean(String(visitor.relationship || '').trim()),
    );
  }

  private buildFamilyMappings(): any[] {
    return this.editMembers.map((member) => ({
      id: member.devoteeId,
      gender: member.gender,
      relationship: member.devoteeId === this.primaryDevotee?.devoteeId
        ? 'SELF'
        : member.relationshipWithPrimaryDevotee,
    }));
  }

  private buildVisitorMappings(): any[] {
    return this.editVisitor.map((visitor) => ({
      id: visitor.visitorId,
      gender: visitor.gender,
      relationship: visitor.relationship,
    }));
  }

  private upsertMapping(
    target: any[],
    id: unknown,
    gender: unknown,
    relationship: string,
  ): void {
    const index = target.findIndex((mapping) => mapping.id === id);
    if (!relationship) {
      if (index >= 0) target.splice(index, 1);
      return;
    }
    const mapping = { id, gender, relationship };
    if (index >= 0) target[index] = mapping;
    else target.push(mapping);
  }

  private stepsForVisitors(hasVisitors: boolean): string[] {
    return [
      this.familyStage,
      ...(hasVisitors ? [this.visitorStage] : []),
      this.contactStage,
      this.addressStage,
      this.agreementStage,
    ];
  }

  private hasValidAddressLocation(): boolean {
    const address = this.addressfields.getRawValue();
    const isLocationVerified = this.pincodeLookup.isVerified(address.pincode);
    const hasResolvedLocation = Boolean(address.state && address.district);
    if (this.addressfields.invalid || !isLocationVerified || !hasResolvedLocation) {
      this.addressfields.markAllAsTouched();
      if (!isLocationVerified || !hasResolvedLocation) {
        this.toaster.error(
          this.pincodeLookup.message || 'Enter a valid, verified 6-digit pincode.',
        );
      }
      return false;
    }
    return true;
  }

  private cancelFlowRequests(): void {
    this.visitorRequest?.unsubscribe();
    this.contactNumbersRequest?.unsubscribe();
    this.addressRequest?.unsubscribe();
    this.availabilityRequest?.unsubscribe();
    this.submitRequest?.unsubscribe();
    this.visitorRequest = undefined;
    this.contactNumbersRequest = undefined;
    this.addressRequest = undefined;
    this.availabilityRequest = undefined;
    this.submitRequest = undefined;
  }
}
